import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  USER_NOT_EXIST_ERR,
  resetPasswordValidationSchema,
} from "../../../constants/auth";
import { resetPassword } from "../../../services/apiV2";
import { USER_NOT_EXIST, RESET_PASSWORD_FAILED } from "../../../constants";
import { axiosErrorMatches } from "../../../helpers";

type TFormFields = {
  email: string;
};

type TErrors = {
  userNotExist: string | null;
  resetPasswordFailed: string | null;
};

type TUseResetPasswordProps = {
  onSuccess: ({ email }: { email: string }) => void;
};

const ERRORS_INITIAL_STATE: TErrors = {
  userNotExist: null,
  resetPasswordFailed: null,
};

const passwordRedirectUrl = `${process.env.REACT_APP_SERVER_URL}/reset-password?token=`;

export const useResetPassword = ({ onSuccess }: TUseResetPasswordProps) => {
  const [errors, setErrors] = useState<TErrors>(ERRORS_INITIAL_STATE);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<TFormFields>({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(resetPasswordValidationSchema),
    reValidateMode: "onSubmit",
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    setErrors(ERRORS_INITIAL_STATE);

    try {
      await resetPassword({ email, passwordRedirectUrl });
      onSuccess({ email });
    } catch (e) {
      if (axiosErrorMatches(e, USER_NOT_EXIST_ERR)) {
        setErrors((prev) => ({
          ...prev,
          userNotExist: USER_NOT_EXIST,
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          resetPasswordFailed: RESET_PASSWORD_FAILED,
        }));
      }
    }
  });

  return {
    errors,
    control,
    isSubmitting,
    onSubmit,
  };
};
